import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../admin/sessionSlice";
import "./leaderboard.css";
import { setBackButtonUrl } from '../uiSlice';

const hasValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  const normalized = text.toLowerCase();
  return normalized !== "null" && normalized !== "undefined" && normalized !== "null null";
};

const normalizeIdentity = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isPlaceholderValue = (value) => {
  const normalized = normalizeIdentity(value);
  return (
    !normalized ||
    normalized === "—" ||
    normalized === "-" ||
    normalized === "null" ||
    normalized === "undefined"
  );
};

const looksLikeEmail = (value) => String(value ?? "").includes("@");
const isNumericOnly = (value) => /^\d+$/.test(String(value ?? "").trim());

const MedalIcon = ({ rank }) => <span className="rank-number">{rank}</span>;

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user } = useSelector((state) => state.auth);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const retryCountRef = useRef(0);


  useEffect(() => {
    dispatch(setBackButtonUrl("/game"));
  }, [status, user]);


  const colSpan = 4;
  const hasPlayerNames = list.some(
    (row) =>
      row?.name != null &&
      String(row.name).trim() !== "" &&
      String(row.name).trim().toLowerCase() !== "null null"
  );
  const hasEmailValues = list.some((row) => looksLikeEmail(row?.email));
  const firstColumnHeader = hasPlayerNames
    ? "Player Name"
    : hasEmailValues
      ? "Email"
      : "Emp ID";

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const fetchLeaderboard = useCallback(async () => {
    if (!adminToken || !sessionId || !organizationId) {
      setLoading(false);
      return null;
    }
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          sessionId,
          organizationId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setError(null);
        setList(data.data);
        return data.data;
      }
      const errMsg = data.error || "Failed to load leaderboard";
      setError(errMsg);
      return null;
    } catch (e) {
      setError(e.message || "Failed to load leaderboard");
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminToken, sessionId, organizationId]);

  const hasIdentityData = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    return rows.some((row) => hasValue(row?.name) || hasValue(row?.email));
  }, []);

  const currentUserIdentity = {
    id: String(user?.userId ?? user?.id ?? "").trim(),
    email: normalizeIdentity(user?.email),
    name: normalizeIdentity(user?.name),
  };

  const hasCurrentUserIdentity = Boolean(
    currentUserIdentity.id || currentUserIdentity.email || currentUserIdentity.name
  );

  const isCurrentUserRow = useCallback(
    (row) => {
      const rowUserId = String(row?.userId ?? "").trim();
      const rowEmail = normalizeIdentity(row?.email);
      const rowName = normalizeIdentity(row?.name);
      const hasUserId =
        !isPlaceholderValue(currentUserIdentity.id) && !isPlaceholderValue(rowUserId);
      if (hasUserId) {
        return rowUserId === currentUserIdentity.id;
      }

      const hasEmail =
        !isPlaceholderValue(currentUserIdentity.email) &&
        !isPlaceholderValue(rowEmail) &&
        looksLikeEmail(currentUserIdentity.email);
      if (hasEmail) {
        return rowEmail === currentUserIdentity.email;
      }

      const hasName =
        !isPlaceholderValue(currentUserIdentity.name) &&
        !isPlaceholderValue(rowName) &&
        currentUserIdentity.name.length >= 3;
      if (hasName) {
        return rowName === currentUserIdentity.name;
      }

      return false;
    },
    [currentUserIdentity.id, currentUserIdentity.email, currentUserIdentity.name]
  );

  useEffect(() => {
    let cancelled = false;
    retryCountRef.current = 0;
    setLoading(true);

    const loadWithRetry = async () => {
      const data = await fetchLeaderboard();
      if (cancelled) return;
      const hasRows = Array.isArray(data) && data.length > 0;
      const hasNamesOrEmails = hasIdentityData(data);
      const hasCurrentUserRow =
        hasRows && hasCurrentUserIdentity ? data.some((row) => isCurrentUserRow(row)) : true;
      if (hasRows && hasNamesOrEmails && hasCurrentUserRow) return;

      // Sometimes rows are returned first, then player identity fields arrive shortly after.
      if (retryCountRef.current < 5) {
        retryCountRef.current += 1;
        window.setTimeout(() => {
          if (!cancelled) {
            void loadWithRetry();
          }
        }, 1500);
      }
    };

    void loadWithRetry();
    return () => {
      cancelled = true;
    };
  }, [fetchLeaderboard, hasIdentityData, hasCurrentUserIdentity, isCurrentUserRow]);

  useEffect(() => {
    if (!hasCurrentUserIdentity || loading || error) return undefined;
    if (list.some((row) => isCurrentUserRow(row))) return undefined;

    // Backend stat write can lag a few seconds; poll briefly so refresh is not required.
    let attempts = 0;
    let cancelled = false;
    const maxAttempts = 8;
    const timer = window.setInterval(() => {
      attempts += 1;
      void (async () => {
        const data = await fetchLeaderboard();
        if (cancelled || !Array.isArray(data)) return;
        if (data.some((row) => isCurrentUserRow(row)) || attempts >= maxAttempts) {
          window.clearInterval(timer);
        }
      })();
      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    error,
    fetchLeaderboard,
    hasCurrentUserIdentity,
    isCurrentUserRow,
    list,
    loading,
  ]);

  const sortedUsers = [...list].sort((a, b) => {
    const rankA = Number(a?.rank);
    const rankB = Number(b?.rank);
    const scoreA = Number(a?.score ?? 0);
    const scoreB = Number(b?.score ?? 0);

    const hasRankA = Number.isFinite(rankA) && rankA > 0;
    const hasRankB = Number.isFinite(rankB) && rankB > 0;

    if (hasRankA && hasRankB && rankA !== rankB) return rankA - rankB;
    if (hasRankA !== hasRankB) return hasRankA ? -1 : 1;
    if (scoreB !== scoreA) return scoreB - scoreA;
    return String(a?.name ?? a?.email ?? "").localeCompare(
      String(b?.name ?? b?.email ?? "")
    );
  });

  const topUsers = sortedUsers.slice(0, 5);
  console.log(topUsers);
  const displayName = (() => {
    const rawName = String(user?.name ?? "").trim();
    if (hasValue(rawName) && !isNumericOnly(rawName)) return rawName;
    const rawEmail = String(user?.email ?? "").trim();
    if (looksLikeEmail(rawEmail)) return rawEmail.split("@")[0] || "Player";
    return "Player";
  })();

  const getPrimaryCellValue = (row) => {
    const name = hasValue(row?.name) ? String(row.name).trim() : "";
    const email = hasValue(row?.email) ? String(row.email).trim() : "";

    if (name) return name;
    return email || "—";
  };

  const currentUserRankInfo = (() => {
    if (!hasCurrentUserIdentity) return null;

    const idx = sortedUsers.findIndex((row) => {
      return isCurrentUserRow(row);
    });

    if (idx < 0) return null;
    const row = sortedUsers[idx];
    return {
      rank: Number(row?.rank ?? idx + 1),
      score: Number(row?.score ?? 0),
      streak: Number(row?.good ?? 0),
      bad: Number(row?.bad ?? 0),
      name: getPrimaryCellValue(row),
    };
  })();

  const getRowClass = (rank) => {
    if (rank === 1) return "row-gold";
    if (rank === 2) return "row-silver";
    if (rank === 3) return "row-bronze";
    return "";
  };

  const harmfulItemsSmashed = Number(currentUserRankInfo?.bad ?? 0);
  const goodItemsAvoided = Number(currentUserRankInfo?.streak ?? 0);
  const textColor = themeData?.textColor || "#2f5d3a";

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-layout">
        <div className="leaderboard-nature-container">
          <div className="leaf-decoration leaf-top-left"></div>
          <div className="leaf-decoration leaf-top-right"></div>
          <div className="leaf-decoration leaf-bottom-left"></div>
          <div className="leaf-decoration leaf-bottom-right"></div>
          
          <div className="leaderboard-table-container">
            {loading ? (
              <div className="leaderboard-loading">Loading...</div>
            ) : error ? (
              <div className="leaderboard-error">{error}</div>
            ) : (
              <>
                <div className="leaderboard-table-wrap">
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>{firstColumnHeader}</th>
                        <th>Score</th>
                        <th>Smash streak 🔥</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topUsers.length === 0 ? (
                        <tr>
                          <td colSpan={colSpan} className="leaderboard-empty">
                            No leaderboard data available.
                          </td>
                        </tr>
                      ) : (
                        topUsers.map((row) => {
                          const rank = row.rank ?? topUsers.indexOf(row) + 1;
                          return (
                            <tr 
                              key={`${row.rank}-${row.userId}-${row.email}`}
                              className={getRowClass(rank)}
                            >
                              <td className="rank-cell">
                                <MedalIcon rank={rank} />
                              </td>
                              <td className="name-cell">
                                {getPrimaryCellValue(row)}
                              </td>
                              <td className="score-cell">{row.score ?? 0}</td>
                              <td className="streak-cell">
                                <span className="fire-icon">🔥</span> {row.smashStreak ?? 0}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {currentUserRankInfo && currentUserRankInfo.rank > 10 && (
                  <div className="your-rank-section">
                    <div className="your-rank-badge">
                      <span className="badge-icon">👤</span>
                      <span className="badge-text">Your<br/>rank</span>
                    </div>
                    <div className="your-rank-number">{currentUserRankInfo.rank}</div>
                    <div className="your-rank-name">
                      {currentUserRankInfo.name || displayName}
                    </div>
                    <div className="your-rank-score">{currentUserRankInfo.score}</div>
                    <div className="your-rank-streak">
                      <span className="fire-icon">🔥</span> {currentUserRankInfo.smashStreak}
                    </div>
                  </div>
                )}

                <p
                  className="leaderboard-impact-message leaderboard-impact-top"
                  style={{ color: textColor ,textAlign:'center'}}
                >
                  You smashed {harmfulItemsSmashed} harmful items and avoided{" "}
                  {goodItemsAvoided} good ones. You helped protect the
                  environment! <span role="img" aria-label="earth">🌍</span>{" "}
                  Every action counts.
                </p>

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
